import { type AppointmentChainConfig, type StepEligiblePractitioner } from '@/models/appointment-chain-config.ts';

import { getEligibleMainPractitioners } from '@/utils/chain-main-practitioner-utils.ts';

export interface ChainPatientTypeAvailability {
    supportsNew: boolean;
    supportsReturning: boolean;
}

export const chainPatientTypeAvailability = (config: AppointmentChainConfig): ChainPatientTypeAvailability => ({
    supportsNew: config.is_for_new_patients,
    supportsReturning: config.is_for_returning_patients,
});

// isReturning convention (matches PatientTypeSelection.setPatientType): false = new, true = returning.
export const isChainCompatibleWithPatientType = (config: AppointmentChainConfig, isReturning: boolean): boolean => {
    const { supportsNew, supportsReturning } = chainPatientTypeAvailability(config);
    return isReturning ? supportsReturning : supportsNew;
};

/** Slice of the availability-search professional object the practitioner-results page reads off each `doctor`. */
export interface ChainAvailableProfessional {
    id: string;
    key: { text: string }[];
    display_name: { text: string }[];
    practice_name: { text: string }[];
    // Per-language shape `ProfessionalResult` reads via `lang.languages1`.
    languages: { languages1: string }[];
    profile_picture: { url: string | null; alt: string };
    is_bookable_by_patient: boolean;
    is_for_new_patients: boolean;
    is_for_returning_patients: boolean;
    practitioner_id: string;
    practitioner_pms_id: string;
    calendar_pms_ids: string[];
    prismic_key: string;
}

/**
 * Maps one eligible practitioner to the practitioner-results page shape. Patient-type flags are
 * chain-level ({@link chainPatientTypeAvailability}) and stamped identically on every card.
 */
export const toAvailableProfessional = (
    practitioner: StepEligiblePractitioner,
    config: AppointmentChainConfig
): ChainAvailableProfessional => {
    const { supportsNew, supportsReturning } = chainPatientTypeAvailability(config);

    return {
        id: practitioner.practitioner_id,
        key: [{ text: practitioner.prismic_key }],
        display_name: [{ text: practitioner.display_name }],
        practice_name: [{ text: '' }],
        languages: [],
        // `null` (not '') so React drops `src` rather than emitting `src=""` and showing a broken image.
        profile_picture: { url: null, alt: practitioner.display_name },
        is_bookable_by_patient: config.is_bookable_by_patient,
        is_for_new_patients: supportsNew,
        is_for_returning_patients: supportsReturning,
        practitioner_id: practitioner.practitioner_id,
        practitioner_pms_id: practitioner.practitioner_pms_id,
        calendar_pms_ids: practitioner.calendar_pms_ids,
        prismic_key: practitioner.prismic_key,
    };
};

/** Maps a chain's eligible practitioners (all steps, deduped by id) to the practitioner-results page shape. */
export const mapChainToAvailableProfessionals = (config: AppointmentChainConfig): ChainAvailableProfessional[] => {
    const byPractitionerId = new Map<string, StepEligiblePractitioner>();
    config.steps.forEach((step) => {
        step.eligible_practitioners.forEach((practitioner) => {
            if (!byPractitionerId.has(practitioner.practitioner_id)) {
                byPractitionerId.set(practitioner.practitioner_id, practitioner);
            }
        });
    });

    return [...byPractitionerId.values()].map((practitioner) => toAvailableProfessional(practitioner, config));
};

/**
 * Practitioners shared across EVERY `is_main_practitioner` step (the intersection, NOT the
 * all-steps dedupe above), mapped to the card shape.
 */
export const mapEligibleMainPractitionersToAvailableProfessionals = (
    config: AppointmentChainConfig
): ChainAvailableProfessional[] =>
    getEligibleMainPractitioners(config).map((practitioner) => toAvailableProfessional(practitioner, config));
